import React from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../store';
import {
  PREFERENCE_KEYS,
  saveUserPreferences,
  selectPrivacyPrefs,
} from '../../store/slices/userPreferences';
import { getTrackingConsent } from '@/nova/pages/configurator-v3/lib/analytics';
import { applyMarketingConsent, trackPageView } from '@/utils/analytics';
import { resolveMarketingConsent } from './resolveMarketingConsent';

/**
 * App-wide, consent-gated analytics wiring. Mounted once in `BaseElement` (routes.tsx), so it
 * spans every route — login, register, configurator, studio, config-driven pages.
 *
 * Marketing consent (Google Analytics + the Facebook Pixel + the Reddit Pixel) is ON BY DEFAULT and
 * is resolved from the cross-device user-preferences `privacy` namespace when available, falling
 * back to the browser-scoped `ows-tracking-consent` record (which itself falls back to the
 * `startProperties` hand-off). An explicit choice from either source is respected (a marketing-site
 * opt-out, or a Settings toggle); tracking is only defaulted ON when NO choice exists anywhere. See
 * `resolveMarketingConsent`. While authenticated but the prefs haven't loaded yet, consent is
 * treated as denied so we never fire on a stale localStorage value and then revoke.
 *
 * It also SEEDS the consent into user-preferences once: the first time an authed owner's prefs
 * are loaded with no `privacy` namespace yet, the `startProperties`/localStorage choice is
 * persisted via the API (so it follows them cross-device). Gating on "no saved value yet"
 * prevents a stale marketing-site link from silently overwriting a later Settings choice.
 *
 * Renders nothing.
 */
const AnalyticsConsent = (): null => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authenticated = useAppSelector((state) => state.authentication.user.user_id != null);
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const privacy = useAppSelector(selectPrivacyPrefs);

  // Effective marketing consent — ON by default, an explicit saved / handed-off choice wins. The
  // prefs-loading guard + `??` precedence live in the pure helper (unit-tested).
  const granted = resolveMarketingConsent({
    authenticated,
    prefsLoaded,
    savedMarketing: privacy?.marketing,
    handoffMarketing: getTrackingConsent()?.marketing,
  });

  // Load / enable / disable the trackers. Declared BEFORE the page-view effect so, on a render
  // where consent flips on, the SDK stubs are defined before the first page view is emitted.
  // Re-runs on route change (idempotent) because the Pixel load is DEFERRED while the URL
  // carries a sensitive param (see applyMarketingConsent) — the first clean URL loads it.
  React.useEffect(() => {
    applyMarketingConsent(granted);
  }, [granted, location.pathname, location.search]);

  // SPA page views — the single owner of every page view (GA is initialised with
  // `send_page_view: false`, the Pixel init fires no view), so there's no first-load double.
  React.useEffect(() => {
    if (granted) trackPageView(location.pathname + location.search);
  }, [granted, location.pathname, location.search]);

  // One-time seed of the hand-off consent into user-preferences (see the doc comment above).
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current || !authenticated || !prefsLoaded || privacy !== null) return;
    const consent = getTrackingConsent();
    if (!consent) return; // nothing was handed off — leave the namespace unset (the gate defaults it ON)
    seededRef.current = true;
    void dispatch(saveUserPreferences({ [PREFERENCE_KEYS.privacy]: { marketing: consent.marketing } }));
  }, [authenticated, prefsLoaded, privacy, dispatch]);

  return null;
};

export default AnalyticsConsent;
